// src/services/visitorCallbackServices.js

import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/visitor-callback-requests`;

function getAuthHeaders() {
  const token = localStorage.getItem("token");

  if (!token) {
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

function handleServiceError(message, error) {
  console.error(message, error);

  if (error?.response?.data) {
    console.error("Réponse API :", error.response.data);
  }

  throw error;
}

/* -------------------------------------------------------------------------- */
/* ROUTE PUBLIQUE                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Crée une demande de rappel depuis le widget public.
 *
 * @param {Object} callbackData
 * @returns {Promise<Object>}
 */
export const createVisitorCallbackRequest = async (
  callbackData,
) => {
  try {
    const response = await axios.post(apiUrl, callbackData);

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la création de la demande de rappel :",
      error,
    );
  }
};

/* -------------------------------------------------------------------------- */
/* ROUTES ADMINISTRATEUR                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Récupère les demandes de rappel.
 *
 * Filtres disponibles :
 * - page
 * - limit
 * - status
 * - q
 *
 * @param {Object} params
 * @returns {Promise<Object>}
 */
export const getVisitorCallbackRequests = async (params = {}) => {
  try {
    const response = await axios.get(apiUrl, {
      headers: getAuthHeaders(),
      params,
    });

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération des demandes de rappel :",
      error,
    );
  }
};

/**
 * Récupère le détail d'une demande de rappel.
 *
 * @param {number|string} callbackRequestId
 * @returns {Promise<Object>}
 */
export const getVisitorCallbackRequestById = async (
  callbackRequestId,
) => {
  try {
    if (!callbackRequestId) {
      throw new Error(
        "L'identifiant de la demande de rappel est requis.",
      );
    }

    const response = await axios.get(
      `${apiUrl}/${callbackRequestId}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la récupération de la demande de rappel :",
      error,
    );
  }
};

/**
 * Modifie une demande de rappel.
 *
 * Champs possibles :
 * - status
 * - linked_prospect_id
 * - name
 * - organization_name
 * - email
 * - phone
 * - preferred_date
 * - preferred_time
 * - product_interest
 * - message
 *
 * @param {number|string} callbackRequestId
 * @param {Object} callbackData
 * @returns {Promise<Object>}
 */
export const updateVisitorCallbackRequest = async (
  callbackRequestId,
  callbackData,
) => {
  try {
    if (!callbackRequestId) {
      throw new Error(
        "L'identifiant de la demande de rappel est requis.",
      );
    }

    const response = await axios.patch(
      `${apiUrl}/${callbackRequestId}`,
      callbackData,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la modification de la demande de rappel :",
      error,
    );
  }
};

/**
 * Supprime définitivement une demande de rappel.
 *
 * @param {number|string} callbackRequestId
 * @returns {Promise<Object>}
 */
export const deleteVisitorCallbackRequest = async (
  callbackRequestId,
) => {
  try {
    if (!callbackRequestId) {
      throw new Error(
        "L'identifiant de la demande de rappel est requis.",
      );
    }

    const response = await axios.delete(
      `${apiUrl}/${callbackRequestId}`,
      {
        headers: getAuthHeaders(),
      },
    );

    return response.data;
  } catch (error) {
    return handleServiceError(
      "Erreur lors de la suppression de la demande de rappel :",
      error,
    );
  }
};

/**
 * Marque une demande comme contactée.
 *
 * @param {number|string} callbackRequestId
 * @returns {Promise<Object>}
 */
export const markVisitorCallbackAsContacted = async (
  callbackRequestId,
) => {
  return updateVisitorCallbackRequest(callbackRequestId, {
    status: "contacted",
  });
};

/**
 * Marque une demande comme convertie en prospect ou client.
 *
 * @param {number|string} callbackRequestId
 * @param {number|string|null} prospectId
 * @returns {Promise<Object>}
 */
export const markVisitorCallbackAsConverted = async (
  callbackRequestId,
  prospectId = null,
) => {
  return updateVisitorCallbackRequest(callbackRequestId, {
    status: "converted",
    linked_prospect_id: prospectId,
  });
};

/**
 * Ferme une demande de rappel.
 *
 * @param {number|string} callbackRequestId
 * @returns {Promise<Object>}
 */
export const closeVisitorCallbackRequest = async (
  callbackRequestId,
) => {
  return updateVisitorCallbackRequest(callbackRequestId, {
    status: "closed",
  });
};

/**
 * Annule une demande de rappel.
 *
 * @param {number|string} callbackRequestId
 * @returns {Promise<Object>}
 */
export const cancelVisitorCallbackRequest = async (
  callbackRequestId,
) => {
  return updateVisitorCallbackRequest(callbackRequestId, {
    status: "cancelled",
  });
};